"use client";
import React, { useState, useEffect, InputHTMLAttributes, useRef } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@/components/buttons"; // Assuming Button component is correctly imported
import cn from "@/utils/cn";

import { MdOutlineChevronLeft } from "react-icons/md";

interface LocalInputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
}

const LocalInputField = React.forwardRef<
  HTMLInputElement,
  LocalInputFieldProps
>(({ name, type = "text", placeholder, className, required, ...rest }, ref) => {
  const { register } = useFormContext();
  const { ref: rhfRef, ...registerProps } = register(name);

  return (
    <input
      id={name}
      type={type}
      placeholder={placeholder}
      required={required}
      {...registerProps}
      {...rest}
      ref={(e) => {
        rhfRef(e);
        if (typeof ref === "function") {
          ref(e);
        } else if (ref && typeof ref === "object") {
          ref.current = e;
        }
      }}
      className={cn(
        "h-[40px] grow rounded-lg bg-white px-3 xl:text-sm text-[9.5px] text-gray-900 placeholder:text-gray-500 focus:border-[#1CE783] focus:outline-none transition-colors duration-150 ease-in-out",
        className,
      )}
    />
  );
});
LocalInputField.displayName = "LocalInputField";

const formSchema = z.object({
  email: z
    .string()
    .email("Please enter a valid email address.")
    .min(1, "Email is required."),
  apiKey: z
    .string()
    .min(1, "API Key is required.")
    .regex(
      /^[a-f0-9]{32}-us[0-9]{1,2}$/i,
      "Please enter a valid Mailchimp API key.",
    ),
});

type FormData = z.infer<typeof formSchema>;

type StandaloneAuditFormProps = {
  showApiKey: boolean;
  setShowApiKey: (showApiKey: boolean) => void;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export default function StandaloneAuditForm({
  showApiKey = false,
  setShowApiKey,
  className,
  ...props
}: StandaloneAuditFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<{
    type: "success" | "error" | null;
    message: string | null;
  }>({ type: null, message: null });

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    trigger,
    watch,
    setValue,
    reset,
    formState: { errors, touchedFields }, // Added touchedFields
    clearErrors, // Added clearErrors
  } = methods;

  const emailValue = watch("email");
  const apiKeyFieldValue = watch("apiKey");

  const apiKeyInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (submissionStatus.message) {
      timeoutId = setTimeout(() => {
        setSubmissionStatus({ type: null, message: null });
      }, 7000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [submissionStatus.message]);

  useEffect(() => {
    // Focus management when steps change
    const focusDelay = 150; // Sync with animation or slightly after
    let timer: NodeJS.Timeout;

    if (showApiKey) {
      timer = setTimeout(() => {
        apiKeyInputRef.current?.focus();
      }, focusDelay);
    } else {
      timer = setTimeout(() => {
        emailInputRef.current?.focus();
      }, focusDelay);
    }
    return () => clearTimeout(timer);
  }, [showApiKey]);

  const handleContinue = async () => {
    setSubmissionStatus({ type: null, message: null });
    const isEmailValid = await trigger("email");
    if (isEmailValid) {
      setValue("apiKey", "");
      clearErrors("apiKey"); // Clear any previous API key errors
      setShowApiKey(true);
    }
  };

  const handleGoBack = () => {
    setSubmissionStatus({ type: null, message: null }); // Clear any messages
    setShowApiKey(false);
    setValue("apiKey", ""); // Clear API key value
    clearErrors("apiKey"); // Clear API key errors
    // Focus will be handled by the useEffect for showApiKey
  };

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setSubmissionStatus({ type: null, message: null });

    try {
      const response = await fetch("/api/forms/submit-and-fetch", {
        method: "POST",
        body: JSON.stringify({
          ...data,
          mailchimpKey: data.apiKey,
          formId: "STANDALONE_AUDIT_FORM",
          sourceUrl: typeof window !== "undefined" ? window.location.href : "",
        }),
      });

      if (!response.ok) {
        let errorMessage = "Submission failed. Please try again.";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      setSubmissionStatus({
        type: "success",
        message: "Report generation started! We'll email you shortly.",
      });
      reset();
      setShowApiKey(false);
    } catch (error: any) {
      setSubmissionStatus({
        type: "error",
        message: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  let topMessage = submissionStatus.message;
  let topMessageType = submissionStatus.type;

  if (!topMessage) {
    if (!showApiKey && errors.email?.message && touchedFields.email) {
      topMessage = errors.email.message;
      topMessageType = "error";
    } else if (showApiKey && errors.apiKey?.message && touchedFields.apiKey) {
      topMessage = errors.apiKey.message;
      topMessageType = "error";
    }
  }

  const animationDuration = "duration-500";
  const animationEasing = "ease-in-out";

  return (
    <div className={cn(className)} {...props}>
      {topMessage && (
        <div
          className={cn(
            "text-[8px] xl:text-[12px] px-3 mb-1 rounded-md",
            topMessageType === "success" ? "text-[#00E096]" : "",
            topMessageType === "error" ? "text-[#FA5A7D]" : "",
          )}
          role="alert"
        >
          {topMessage}
        </div>
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div
            className={cn(
              "bg-white rounded-[12px] xl:rounded-2xl w-full mx-auto xl:w-[576px]",
              className?.includes("xl:w-[") ? "" : className,
            )}
          >
            <div
              className={cn("relative h-[40px] xl:h-[60px] overflow-hidden")}
            >
              {/* Step 1: Email Input */}
              <div
                className={cn(
                  "absolute inset-0 flex items-center space-x-2 px-2 transition-all",
                  animationDuration,
                  animationEasing,
                  showApiKey
                    ? "-translate-y-full opacity-0"
                    : "translate-y-0 opacity-100",
                )}
                aria-hidden={showApiKey}
              >
                <LocalInputField
                  ref={emailInputRef}
                  name="email"
                  type="email"
                  placeholder="Enter you email address to claim a free audit!"
                  required
                  tabIndex={showApiKey ? -1 : 0}
                />
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={isLoading || !emailValue || !!errors.email}
                  className="h-[30px] shrink-0 cursor-pointer text-nowrap rounded-lg bg-[#00E096] px-2 text-[10.5px] font-medium text-white transition-colors hover:bg-[#18c371] hover:text-white active:bg-[#15a863] xl:h-[40px] xl:px-5 xl:text-[16px]"
                  tabIndex={showApiKey ? -1 : 0}
                >
                  Audit
                </Button>
              </div>

              {/* Step 2: API Key Input */}
              <div
                className={cn(
                  "absolute inset-0 flex items-center space-x-2 px-2 transition-all",
                  animationDuration,
                  animationEasing,
                  showApiKey
                    ? "translate-y-0 opacity-100"
                    : "translate-y-full opacity-0",
                )}
                aria-hidden={!showApiKey}
              >
                <button
                  type="button"
                  onClick={handleGoBack}
                  aria-label="Go back to email input"
                  className="shrink-0 rounded-md p-1.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800 active:bg-gray-200 xl:p-2"
                  tabIndex={!showApiKey ? -1 : 0}
                >
                  <MdOutlineChevronLeft className="size-5 xl:size-6" />
                </button>
                <LocalInputField
                  ref={apiKeyInputRef}
                  name="apiKey"
                  type="text"
                  placeholder="Enter you Mailchimp API key"
                  required
                  tabIndex={!showApiKey ? -1 : 0}
                />
                <Button
                  type="submit"
                  disabled={isLoading || !apiKeyFieldValue || !!errors.apiKey}
                  className="h-[30px] shrink-0 cursor-pointer text-nowrap rounded-lg bg-[#00E096] px-2 text-[10.5px] font-medium text-white transition-colors hover:bg-[#18c371] hover:text-white active:bg-[#15a863] xl:h-[40px] xl:px-5 xl:text-[16px]"
                  tabIndex={!showApiKey ? -1 : 0}
                >
                  {isLoading ? "Generating..." : "Generate Report"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
